import React, { useState, useEffect } from "react";
import { User, UserRole, Mutation, MutationUpdateUserArgs } from "@/app/graphql/codegen";
import { icon } from "@/app/style/icons";
import {
    Grid2,
    Avatar,
    Typography,
    IconButton,
    Select,
    MenuItem,
    Tooltip,
    InputBase,
    useTheme,
    useMediaQuery,
    Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PageQuotaEditable } from "./PageQuotaEditable";
import { useMutation } from "@apollo/client";
import { UPDATE_USER_PROFILE } from "@company/graphql/mutations/updateUserProfile";
import { color } from "@/app/style/color";
import { useDispatch } from "react-redux";
// Removed redux imports for legacy seat switch

export type UserSize = "S" | "M" | "L";

interface UserManagementRowProps {
    user: User;
    quota: number;
    onQuotaChange: (value: number) => void;
    role: UserRole;
    onRoleChange: (role: UserRole) => void;
    size: UserSize;
    onSizeChange: (size: "S" | "M" | "L") => void;
    isOwner: boolean;
    onDeleteClick: (user: User) => void;
    onSwapClick?: (user: User) => void;
    isQuotaInvalid?: boolean;
    disableSizeEdit?: boolean;
    sizeColor?: string;
}

export const UserManagementRow: React.FC<UserManagementRowProps> = ({
    user,
    quota,
    onQuotaChange,
    role,
    onRoleChange,
    size,
    onSizeChange,
    isOwner,
    onDeleteClick,
    onSwapClick,
    isQuotaInvalid = false,
    disableSizeEdit = false,
    sizeColor,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    // Local state for inline editing
    const [editName, setEditName] = useState(user.name);
    const [editTitle, setEditTitle] = useState(user.title || "");

    // Reset local state when a different user object is passed in (e.g., after seat swap)
    useEffect(() => {
        setEditName(user.name);
        setEditTitle(user.title || "");
    }, [user.id, user.name, user.title]);

    const [updateUser, { loading: updating }] = useMutation<Pick<Mutation, "updateUser">, MutationUpdateUserArgs>(UPDATE_USER_PROFILE, {
        // adjust cache or refetchQueries as needed for your list
        // refetchQueries: [{ query: FETCH_COMPANY_USERS }]
    });

    const handleNameBlur = () => {
        if (editName !== user.name) {
            updateUser({
                variables: {
                    id: user.id,
                    name: editName,
                    title: editTitle,
                },
            });
        }
    };

    const handleTitleBlur = () => {
        if (editTitle !== user.title) {
            updateUser({
                variables: {
                    id: user.id,
                    name: editName,
                    title: editTitle,
                },
            });
        }
    };

    const handleQuotaChange = (newQuota: number) => {
        onQuotaChange(newQuota);
    };

    // Render different layouts based on screen size
    if (isSmallScreen) {
        return (
            <Box
                sx={{
                    width: "100%",
                    boxSizing: "border-box",
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderColor: isQuotaInvalid ? "semantic.notification.error" : sizeColor || theme.palette.semantic.button.primary,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                }}
            >
                {/* Top row: Avatar, Name, Title, Delete button */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar sx={{ backgroundColor: "semantic.surface.primary", mr: 1 }}>{user.name.charAt(0)}</Avatar>

                    <Box sx={{ flex: 1, ml: 1 }}>
                        <InputBase
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={handleNameBlur}
                            disabled={updating}
                            placeholder="Name"
                            sx={{ fontWeight: 600, fontSize: 14, width: "100%" }}
                        />
                        <InputBase
                            size="small"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onBlur={handleTitleBlur}
                            disabled={updating}
                            placeholder="Keine Position"
                            sx={{ fontSize: 13, width: "100%" }}
                        />
                    </Box>

                    {onSwapClick && (
                        <Tooltip title="Sitzplatz tauschen" placement="top">
                            <IconButton 
                                size="small" 
                                onClick={() => onSwapClick(user)}
                            >
                                <icon.switch sx={{ color: "semantic.icon.functional" }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    
                    <Tooltip title={isOwner ? "Firmeninhaber können nicht gelöscht werden" : undefined} placement="top">
                        <span>
                            <IconButton size="small" disabled={isOwner} onClick={() => !isOwner && onDeleteClick(user)}>
                                <icon.delete sx={{ color: "semantic.icon.functional" }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                {/* Bottom row: Role, Size, Quota */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {/* Role Selector */}
                    <Box sx={{ minWidth: "120px", flex: "1 1 30%" }}>
                        {isOwner ? (
                            <Typography variant="body2" sx={{ fontWeight: "bold", color: color.brand.text, p: 1 }}>
                                OWNER
                            </Typography>
                        ) : (
                            <Select fullWidth size="small" value={role || "EDITOR"} onChange={(e) => onRoleChange(e.target.value as UserRole)}>
                                <MenuItem value="ADMIN">ADMIN</MenuItem>
                                <MenuItem value="EDITOR">EDITOR</MenuItem>
                            </Select>
                        )}
                    </Box>

                    {/* Size Selector */}
                    <Box sx={{ minWidth: "100px", flex: "1 1 30%" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                            Größe: <strong>{size}</strong>
                        </Typography>
                    </Box>

                    {/* Page Quota */}
                    <Box sx={{ flex: "1 1 40%", display: "flex", flexDirection: "column" }}>
                        <PageQuotaEditable leftValue={quota - (user.usedPages || 0)} rightValue={quota} onRightChange={handleQuotaChange} isInvalid={isQuotaInvalid} />
                        {isQuotaInvalid && (
                            <Typography variant="caption" color="error">
                                Kontingent muss mindestens {user.usedPages || 0} sein
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    // Regular desktop layout
    return (
        <Grid2
            container
            alignItems={"center"}
            size={12}
            spacing={2}
            p={1.5}
            sx={{
                "&:hover": { bgcolor: "semantic.surface.secondary" },
                borderRadius: 1,
                border: 1,
                borderColor: isQuotaInvalid ? "semantic.notification.error" : sizeColor || theme.palette.semantic.button.primary,
                flexWrap: "nowrap",
            }}
        >
            <Grid2 sx={{ flexShrink: 0 }}>
                <Avatar sx={{ backgroundColor: "semantic.surface.primary" }}>{user.name.charAt(0)}</Avatar>
            </Grid2>

            {/* Name & Title (inline editable) */}
            <Grid2 container spacing={0} sx={{ userSelect: "none", minWidth: 150, flexShrink: 1, flexGrow: 1 }}>
                <Grid2 container pt={0.75} sx={{ userSelect: "none" }}>
                    <Grid2 size={12}>
                        <InputBase
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={handleNameBlur}
                            disabled={updating}
                            placeholder="Name"
                            sx={{ fontWeight: 600, fontSize: 14 }}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <InputBase
                            size="small"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onBlur={handleTitleBlur}
                            disabled={updating}
                            placeholder="Keine Position"
                            sx={{ fontSize: 13 }}
                        />
                    </Grid2>
                </Grid2>
            </Grid2>

            {/* Role Selector */}
            <Grid2 container alignItems={"center"} justifyContent={"flex-end"} sx={{ flexShrink: 0, width: "auto", minWidth: 120 }}>
                {isOwner ? (
                    <Grid2>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: color.brand.text }}>
                            OWNER
                        </Typography>
                    </Grid2>
                ) : (
                    <Grid2>
                        <Select size="small" value={role || "EDITOR"} onChange={(e) => onRoleChange(e.target.value as UserRole)} sx={{ minWidth: 120 }}>
                            <MenuItem value="ADMIN">ADMIN</MenuItem>
                            <MenuItem value="EDITOR">EDITOR</MenuItem>
                        </Select>
                    </Grid2>
                )}
            </Grid2>

            {/* Page Quota */}
            <Grid2 ml={4} sx={{ flexShrink: 0 }}>
                <PageQuotaEditable leftValue={quota - (user.usedPages || 0)} rightValue={quota} onRightChange={handleQuotaChange} isInvalid={isQuotaInvalid} />
                {isQuotaInvalid && (
                    <Typography variant="caption" color="error">
                        Kontingent muss mindestens {user.usedPages || 0} sein
                    </Typography>
                )}
            </Grid2>

            {/* Removed legacy owner-only seat switch button */}

            {/* Swap Button */}
            {onSwapClick && (
                <Grid2>
                    <Tooltip title="Sitzplatz tauschen" placement="top">
                        <IconButton 
                            size="small" 
                            onClick={() => onSwapClick(user)}
                        >
                            <icon.switch sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Tooltip>
                </Grid2>
            )}

            {/* Delete Button */}
            <Grid2 display="flex" alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
                <Tooltip title={isOwner ? "Firmeninhaber können nicht gelöscht werden" : undefined} placement="top">
                    <span>
                        <IconButton size="small" disabled={isOwner} onClick={() => !isOwner && onDeleteClick(user)}>
                            <icon.delete sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Grid2>
        </Grid2>
    );
};
